import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  AfterViewInit,
  Type,
  ViewChild,
  ViewContainerRef,
  Injector,
  inject,
  EnvironmentInjector,
  ElementRef,
  HostListener,
} from '@angular/core';
import { OnDestroy } from '@angular/core';
import { ContextService, MetaService, UtilsService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { EContentPaneFormat, EContentPanelRoute, IContentPane } from 'lingo2-models';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';
import { PaneComponentsBundle } from 'src/pane-components-bundle';
import { AbstractPane } from '../models/abstract-pane';
import isEqual from 'lodash/isEqual';

@Component({
  standalone: true,
  selector: 'app-pane',
  templateUrl: './pane.component.html',
  styleUrls: ['./pane.component.scss'],
  imports: [CommonModule],
})
export class PaneComponent2 extends ChangableComponent implements OnInit, AfterViewInit {
  private _TAG = `[***] [${this.constructor.name}]: `;

  private injector = inject(Injector);
  public deviceService = inject(DeviceDetectorService);
  public utilsService = inject(UtilsService);
  private meta = inject(MetaService);
  public debug$: Observable<boolean>;
  public scrollTimeout: number;
  public stuckState: boolean;
  public isLessonsPage: boolean;
  public topFromHeader: number;
  public isDebug = inject(ContextService).debug;

  @Output() public loadFinished = new EventEmitter<boolean>();
  @ViewChild('viewPane', { read: ViewContainerRef, static: false }) viewPane: ViewContainerRef;
  @ViewChild('panePosition') panePosition: ElementRef;
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.isStuckCategories()) {
      cancelAnimationFrame(this.scrollTimeout);

      this.isLessonsPage = this.componentInstance._pane.baseRoute == EContentPanelRoute.lessons;
      this.scrollTimeout = requestAnimationFrame(() => {
        const headerHeight = document.getElementsByClassName('header')[0]?.clientHeight || 0;
        const titleHeight = document.getElementsByTagName('app-sticky')[1]?.clientHeight - 4 || 0;

        this.topFromHeader = headerHeight + titleHeight;

        const top = this.panePosition?.nativeElement?.getBoundingClientRect()?.top ?? 0;
        const threshold = 1;
        const isStuckNow = Math.abs(top - this.topFromHeader) < threshold;

        if (this.stuckState !== isStuckNow) {
          this.stuckState = isStuckNow;
          this.detectChanges();
        }
      });
    }
  }

  private viewReady = false;
  private componentInstance: AbstractPane;
  private _loadContentRequested = false;
  private _item: IContentPane;
  private _hasContent = false;
  private _isVisible = false;

  public get item(): IContentPane {
    return this._item;
  }

  @Input()
  public set item(value: IContentPane) {
    if (isEqual(this._item, value)) {
      return;
    }
    this._item = value;
    if (this.viewReady && this._item) {
      this.loadComponent();
    }
  }

  public ngOnInit() {
    this.initializeComponent();
  }

  ngAfterViewInit() {
    this.viewReady = true;
    if (this._item) {
      this.loadComponent();
    }
  }
  public get isVisible(): boolean {
    return this._isVisible;
  }
  public get hasContent(): boolean {
    return this._hasContent;
  }
  public get format(): EContentPaneFormat {
    return this.item?.format;
  }
  public get contentLoaded(): boolean {
    return this.componentInstance?.loaded;
  }

  private initializeComponent(): void {
    if (this.item.baseRoute) {
      this.meta.setTitle([this.item.baseRoute + '.meta-title']);
    }
  }
  protected loadContent(): void {
    if (this.componentInstance && !this._loadContentRequested && this.componentInstance.load) {
      this._loadContentRequested = true;
      this.componentInstance.loadPane();
    }
  }

  public onInViewportTopChange(inViewport: { visible: boolean }): void {
    if (inViewport.visible) {
      this.loadContent();
    }
  }

  public onInViewportBottomChange(inViewport: { visible: boolean }): void {
    if (inViewport.visible) {
      this.loadContent();
    }
  }

  public isStuckCategories() {
    return this.deviceService.isMobile() && this.isBrowser && this._item.format === EContentPaneFormat.categories;
  }

  public showMoreInfoDebug(): void {
    console.log(`${this._TAG} More info about featuring pane #` + this._item.order, this._item);
  }

  protected get_component(_format: EContentPaneFormat): Type<any> | null {
    let format = EContentPaneFormat[_format as keyof typeof EContentPaneFormat] ?? _format;
    if (this.deviceService.isMobile() && this.item.replace_for_mobile) {
      if (format === EContentPaneFormat['new-grid-universal-card']) {
        // Change format for mobile (Todo. Maybe refactor with css and just show/hide some div's)
        format = EContentPaneFormat[this.item.replace_for_mobile_pane];
      }
    }

    try {
      const component = PaneComponentsBundle[format];
      if (!component) throw new Error(`Unknown format: ${format}`);
      return component;
    } catch (err) {
      console.error(`${this._TAG}: Create component error: `, err);
      return null;
    }
  }

  public loadComponent(): void {
    const View = this._item;
    const ViewType = View?.format;
    if (!ViewType) {
      return;
    }

    // Vai!
    try {
      const component = this.get_component(ViewType);
      if (!component || !this.viewPane) {
        console.warn(`${this._TAG} Not supported <content-pane> for ${ViewType}: `, component, this.viewPane);
        this._hasContent = false;
        return;
      }

      this.init_view(component, View, this.viewPane);
      this.markForCheck();
    } catch (err) {
      console.debug(`${this._TAG} Failed load component for ${ViewType}`, err);
      this.errorNotificationService.captureError(err, 'OTHER-PROBLEM');
    } finally {
      this._loadContentRequested = false;
    }
  }

  private init_view(comp: Type<any>, pane: IContentPane, ref: ViewContainerRef): any {
    ref.clear();

    const isStandalone = (comp as any)?.['ɵcmp']?.standalone;
    const opts = isStandalone ? { environmentInjector: this.injector.get(EnvironmentInjector) } : undefined;

    const componentRef = ref.createComponent<AbstractPane>(comp, opts);
    this.componentInstance = componentRef.instance;
    this.componentInstance.pane = pane;
    this.setupComponentSubscription();
    this.componentInstance.loadPane();
    return this.componentInstance;
  }

  private setupComponentSubscription(): void {
    const View = this._item;
    const inst = this.componentInstance;
    if (!this._item || !inst) {
      return;
    }

    this._isVisible = View.baseRoute === EContentPanelRoute['my-classes/dashboard'];
    inst.loadFinished
      .pipe(
        catchError(
          (e) => (
            this.errorNotificationService.captureError(e, 'OTHER-PROBLEM'),
            console.error(`${this._TAG}: `, e),
            []
          ),
        ),
        finalize(() => (this._loadContentRequested = false)),
        takeUntil(this.destroyed$),
      )
      .subscribe((has) => {
        const ignore = !!View.always_think_as_ampty;
        this._isVisible = inst.isVisible;
        this._hasContent = View.format === EContentPaneFormat.categories ? false : (!ignore && has) || ignore;
        this.loadFinished.emit(this._hasContent);
      });
  }
}
